import iconLeaderboard from '@assets/overlays/leaderboard/icons/leaderboard-icon.png';
import iconRewards from '@assets/overlays/rewards/icons/rewards-icon.png';
import iconDonorList from '@assets/overlays/donorlist/icons/donorListIcon.png';
import iconTotal from '@assets/progress-bars/icons/total-icon.png';
import iconPoll from '@assets/overlays/poll/icons/poll-icon.png';
import iconMilestones from '@assets/overlays/milestones/icons/milestones-icon.png';
import iconGiftsThatGive from '@assets/overlays/gifts-that-give/icons/gtg-icon.png';
import iconClassic from '@assets/progress-bars/icons/classic-icon.png';
import iconVertical from '@assets/progress-bars/icons/vertical-icon.png';
import iconMax from '@assets/progress-bars/icons/max-icon.png';
import iconMin from '@assets/progress-bars/icons/minimal-icon.png';
import iconDashboard from '@assets/tools/icons/dashboard-icon.png';
import iconBallPit from '@assets/overlays/ball-pit/icons/ball-pit-icon.png';
import iconArtSpace from '@assets/overlays/art-space/icons/art-space-icon.png';
import iconAlerts from '@assets/alerts/icons/sj-alert-icon.png';
import iconDonationTrain from '@assets/overlays/donation-train/donation-train-overlay-icon.png';
import iconSubathonTimer from '@assets/overlays/subathon-timer/icons/subathon-timer.png';
import iconLifetime from '@assets/overlays/lifetime/icons/lifetime-icon.png';

import { isTestMode } from '@util/const.js';
import { PlayliveOverlays } from '@playlive/overlay-config-data';
import { getOverlayIcon } from '@util/common.js';

/**
 * @type {Record<string, Overlay>}
 * */
export const overlays = {
  /**
   * @type {Overlay}
   */
  basic: {
    type: 'basic',
    thumbnail: iconClassic,
    description:
      'Utilize our dynamic Basic Progress Bar overlay to enhance your Tiltify fundraising campaign. Track your progress, recognize donors and promote upcoming milestones and incentives.',
    features: [
      'Fits well into any streaming layouts',
      'Fundraising amount',
      'Goal',
      'Donors, milestones and rewards displayed in scroller',
      'Milestones display every 10 minutes',
      'Slanted and Classic themes',
    ],
    show: true,
    isNew: false,
    isUpdated: false,
    isFeatured: false,
    category: 'progress',
    name: 'Basic Progress Bar',
    link: '/overlays/basic/',
    shortDescription:
      'Track your progress, recognize donors and promote upcoming milestones and incentives.',
    obsDimensions: {
      width: '1120px',
      height: '110px',
    },
  },
  /**
   * @type {Overlay}
   */
  vertical: {
    category: 'progress',
    type: 'vertical',
    name: 'Vertical Progress Bar',
    link: '/overlays/vertical',
    thumbnail: iconVertical,
    show: true,
    isNew: false,
    isUpdated: false,
    isFeatured: false,
    imageClass: 'chat-icon',
    shortDescription: 'Track the progress of your fundraising campaign.',
    description:
      'Utilize our Vertical overlay to enhance your Tiltify fundraising campaign. Easily track the progress of your fundraising campaign.',
    features: [
      'Fits well into any streaming layouts',
      'Easily see the progress of your campaign',
      'Customize progress bar to match your own look and feel',
    ],
    obsDimensions: {
      width: '300px',
      height: '980px',
    },
  },
  /**
   * @type {Overlay}
   */
  max: {
    category: 'progress',
    type: 'max',
    name: 'Max Progress Bar',
    link: '/overlays/max',
    thumbnail: iconMax,
    show: true,
    isNew: false,
    isUpdated: false,
    isFeatured: false,
    description:
      'Utilize our dynamic Max Progress Bar overlay to enhance your Tiltify fundraising campaign. This overlay stretches the full width of your stream, tracks your progress and recognizes donors.',
    shortDescription:
      'This progress bar stretches the full width of your stream, tracks your progress and recognizes donors.',
    features: [
      'Stretches full width of stream',
      'Fundraising amount',
      'Goal',
      'Donors, milestones and rewards displayed in scroller',
      'Milestones display every 10 minutes',
      'Fits most games',
    ],
    obsDimensions: {
      width: '1920px',
      height: '90px',
    },
  },
  /**
   * @type {Overlay}
   */
  min: {
    category: 'progress',
    type: 'min',
    name: 'Minimal Progress Bar',
    link: '/overlays/min',
    thumbnail: iconMin,
    show: true,
    isNew: false,
    isUpdated: false,
    isFeatured: false,
    shortDescription:
      'This progress bar fits any layout and tracks your fundraising total.',
    description:
      'Utilize our dynamic Minimal overlay to enhance your Tiltify fundraising campaign. This overlay fits any layout and tracks your fundraising total.',
    features: [
      'Fundraising amount',
      'Fits most games',
      'Milestones',
      'Takes up less space',
    ],
    obsDimensions: {
      width: '300px',
      height: '95px',
    },
  },
  /**
   * @type {Overlay}
   */
  'gifts-that-give': {
    category: 'overlays',
    type: 'gtg',
    isNew: false,
    show: true,
    isUpdated: false,
    isFeatured: false,
    name: 'Gifts that Give Overlay',
    link: '/overlays/gifts-that-give',
    thumbnail: iconGiftsThatGive,
    description:
      'Every dollar makes a difference. Enhance your fundraising experience by using our Gifts that Give overlay to show the impact your fundraising has for the patients and their families at St. Jude.',
    shortDescription: `Every dollar makes a difference. Show your fundraising impact ${isTestMode ? 'to your viewers' : 'on St. Jude.'}`,
    features: [
      'Automatically generates four Gifts that Give milestones based on your fundraising goal',
      'Easily see those you have completed',
      'Highlights top three donors',
      'Track the progress toward each milestone',
      'Custom colors fit any stream',
    ],
  },
  /**
   * @type {Overlay}
   */
  leaderboard: {
    category: 'overlays',
    type: 'leaderboard',
    name: 'Leaderboard',
    isUpdated: true,
    isFeatured: false,
    show: true,
    isNew: false,
    link: '/overlays/leaderboard',
    thumbnail: iconLeaderboard,
    description:
      'Utilize our dynamic Leaderboard overlay to enhance your Tiltify fundraising campaign. Let your viewers easily see who the top donors are for your St. Jude PLAY LIVE campaign',
    shortDescription:
      'Let your viewers easily see who the top donors are for your campaign.',
    features: [
      '<b>Update: Easily set up leaderboard to show all, daily, weekly, monthly, yearly or year to date.</b>',
      'Easily display your donors on stream',
      'Highlights top three donors',
      'Use your dashboard to remove and show donors',
      'Refreshes automatically',
      'Choose the number of donors you want to display',
    ],
  },
  /**
   * @type {Overlay}
   */
  rewards: {
    category: 'overlays',
    type: 'rewards',
    name: 'Rewards Overlay',
    link: '/overlays/rewards',
    thumbnail: iconRewards,
    isUpdated: false,
    show: true,
    isNew: false,
    isFeatured: false,
    shortDescription:
      'Easily display your Tiltify Rewards on your stream without creating a graphic.',
    description:
      'Utilize our dynamic Rewards overlay to enhance your Tiltify fundraising campaign. Easily display your Tiltify Rewards on your stream without the need of creating a graphic',
    features: [
      '<b>Update: Will feature rewards that you have highlighted on Tiltify</b>',
      'Displays your Tiltify Rewards on stream',
      'Rotates in and out on a timer',
      'Automatically updates when new Rewards are added',
      'Customize colors to fit your stream',
    ],
  },
  /**
   * @type {Overlay}
   */
  polls: {
    category: 'overlays',
    name: 'Polls Overlay',
    link: '/overlays/polls',
    thumbnail: iconPoll,
    type: 'polls',
    shortDescription:
      'Display live results from your Tiltify poll on your stream.',
    show: true,
    isNew: false,
    isUpdated: false,
    isFeatured: false,
    description:
      'Utilize one of our two dynamic poll overlays to track voting results from your Tiltify fundraising campaign. Easily display live results from a head-to-head voting battle or a list of voting options on your stream.',
    features: [
      'Head-to-Head poll option',
      'Poll for multiple options',
      'Poll name displays every 5 minutes',
      'Live vote tracking',
    ],
    obsDimensions: [
      { width: '1920px', height: '70px' },
      { width: 'preferred size', height: 'preferred size' },
    ],
  },
  /**
   * @type {Overlay}
   */
  donorlist: {
    type: 'donorlist',
    category: 'overlays',
    link: '/overlays/donorlist',
    name: 'Donor List',
    thumbnail: iconDonorList,
    shortDescription: 'Display a list of donors on your stream',
    description:
      'Utilize our donor list overlay to enhance your Tiltify fundraising campaign. Allow your viewers to see what has been happening during your fundraising campaign.',
    features: [
      'Fits well into any streaming layouts',
      'Lists recent activities to your viewers',
      'Displays when incentives have been claimed',
      'Vertical and horizontal layout options',
    ],
    show: true,
    isNew: false,
    isUpdated: false,
    isFeatured: false,
    obsDimensions: [
      { width: '500px', height: 'height to fit your list of donors' },
      { width: '1920px', height: '180px' },
    ],
  },
  /**
   * @type {Overlay}
   */
  milestone: {
    category: 'overlays',
    type: 'milestones',
    name: 'Milestone Overlay',
    link: '/overlays/milestone',
    thumbnail: iconMilestones,
    description:
      'Utilize our dynamic Milestone overlay to enhance your Tiltify fundraising campaign. Clearly display your next milestone as well as future ones on your stream.',
    features: [
      'Fits well into any streaming layouts',
      'Easily displays your active milestone',
      'Choose to show all milestones or rotate them in and out',
      'Displays which milestones have been completed',
    ],
    show: true,
    isNew: false,
    isUpdated: false,
    isFeatured: false,
    shortDescription:
      'Clearly display your next milestone as well as future ones on your stream.',
  },
  /**
   * @type {Overlay}
   */
  total: {
    category: 'overlays',
    type: 'event-total',
    name: 'Fundraising Event Tracker',
    link: '/overlays/total',
    thumbnail: iconTotal,
    imageClass: 'chat-icon',
    buttonText: 'Generate',
    shortDescription: 'Track the total amount raised for a specific event.',
    description:
      'Utilize our dynamic total overlay to enhance your fundraising campaign. This overlay fits any layout and tracks the total amount raised throughout a fundraising event.',
    features: [
      'Keeps track of total amount raised for a St. Jude fundraising event',
      'Does not take up a lot of space',
    ],
    show: true,
    isNew: false,
    isUpdated: false,
    isFeatured: false,
  },
  /**
   * @type {Overlay}
   */
  dashboard: {
    show: true,
    isNew: false,
    isUpdated: false,
    isFeatured: false,
    category: 'overlays',
    type: 'dashboard',
    name: 'Fundraising Dashboard',
    link: '/overlays/dashboard',
    thumbnail: iconDashboard,
    imageClass: 'chat-icon',
    shortDescription:
      'Utilize our fundraising dashboard to easily track your fundraising progress.',
    description:
      'Utilize our fundraising dashboard to easily track your fundraising progress.',
    features: [
      '<b>Update: Now displays claimed St. Jude rewards or votes on a poll</b>',
      '<b>Update: Set how many donors to display in your dashboard</b>',
      'Displays total amount raised with your goal',
      'Shows the percentage remaining to complete your goal',
      'Easily see when donors leave a comment, claim a reward or vote in a poll',
      'Tracks your progress toward completing your next milestone',
    ],
  },
  /**
   * @type {Overlay}
   */
  tts: {
    title: 'Donation TTS',
    type: 'tts',
    thumbnail: getOverlayIcon(PlayliveOverlays.tts.details.icon),
    description:
      'Utilize Text-To-Speech to read your donations out loud on stream',
    features: ['Multiple voices', 'Customizable pitch'],
    category: 'tools',
    classname: 'v1',
    name: 'Donation Text To Speech',
    link: '/overlays/tts/',
    show: true,
    isNew: true,
    isUpdated: false,
    isFeatured: false,
    shortDescription: 'Use Text-To-Speech to read your donations out loud on stream.',
    obsDimensions: {
      width: '382px',
      height: '183px',
    },
  },
  /**
   * @type {Overlay}
   */
  'ball-pit': {
    type: 'ball-pit',
    thumbnail: iconBallPit,
    description:
      'Display a physics-enabled ball pit that fills with more balls as more donations come in.',
    features: [
      'Shows scale of donations with ball sizing',
      'Randomized colors',
    ],
    images: [],
    show: true,
    isNew: true,
    isUpdated: false,
    isFeatured: false,
    category: 'overlay',
    name: 'Ball Pit',
    link: '/overlays/ball-pit/',
    shortDescription:
      'Display a physics-enabled ball pit that fills with more balls as more donations come in.',
  },
  /**
   * @type {Overlay}
   */
  'art-space': {
    type: 'art-space',
    thumbnail: iconArtSpace,
    description:
      'Display a physics-enabled space that fills with more art as more donations come in.',
    features: [
      'Shows scale of donations with art sizing',
    ],
    images: [],
    show: true,
    isNew: true,
    isUpdated: false,
    isFeatured: false,
    category: 'overlay',
    name: 'Art Space',
    link: '/overlays/art-space/',
    shortDescription:
      'Display a physics-enabled space that fills with more art as more donations come in.',
  },
  /**
   * @type {Overlay}
   */
  alerts: {
    isFeatured: false,
    isNew: false,
    isUpdated: false,
    category: 'alerts',
    type: 'alerts',
    show: true,
    name: 'Alerts',
    link: '/overlays/alerts',
    thumbnail: iconAlerts,
    description:
      'Utilize our donation alerts to enhance your Tiltify fundraising campaign by acknowledging donations to your fundraising stream.',
    shortDescription:
      'Utilize our donation alerts to acknowledge donations on your stream.',
    features: [
      'St. Jude branded alerts',
      'Hidden Gifts that Give alerts',
      'Different alerts for different donation amounts',
      'Easy to set up',
      'No need for third party integration',
      'Set minimum donation amount to activate alert',
      'Acknowledges your donors on stream',
      'Displays when a reward is claimed',
    ],
  },
  /**
   * @type {Overlay}
   */
  'donation-train': {
    type: 'donation-train',
    show: true,
    isNew: true,
    isUpdated: false,
    isFeatured: false,
    category: 'overlays',
    name: 'Donation Train',
    link: '/overlays/donation-train',
    thumbnail: iconDonationTrain,
    description:
      'Incentivize your viewers to donate to your fundraising campaign by acknowledging donations to your fundraising stream.',
    shortDescription:
      'Incentivize your viewers to donate to your fundraising campaign by acknowledging donations to your fundraising stream.',
    features: [],
  },
  /**
   * @type {Overlay}
   */
  'subathon-timer': {
    type: 'subathon-timer',
    show: true,
    isNew: true,
    isUpdated: false,
    isFeatured: false,
    category: 'overlays',
    name: 'Subathon Timer',
    link: '/overlays/subathon-timer',
    thumbnail: iconSubathonTimer,
    description:
      'The Subathon Timer features an engaging, interactive donation countdown that motivates your viewers to contribute before the timer expires, adding excitement and fun for everyone involved! To use this overlay, simply run it in OBS and watch the donations roll in. The objective is for your audience to keep the timer from reaching ZERO! Each donation is celebrated, highlighting its impact on the countdown.',
    shortDescription:
      'An engaging, interactive donation countdown that motivates your viewers to contribute before the timer expires, adding excitement and fun for everyone involved!',
    features: [],
    obsDimensions: {
      width: '380px',
      height: '200px',
    },
  },
  'lifetime': {
    type:'lifetime',
    show: true,
    isNew: true,
    isUpdated: false,
    isFeatured: false,
    category: 'overlays',
    name: 'Lifetime Raised',
    link: '/overlays/lifetime',
    thumbnail: iconLifetime,
    description:
      'Celebrate your lifetime fundraising achievements on Tiltify for St. Jude Children\'s Research Hospital.',
    shortDescription:
      'Celebrate your lifetime fundraising achievements on Tiltify for St. Jude Children\'s Research Hospital.',
    features: [],
  }
};
